import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircle, PaperPlaneTilt, Copy } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Staff card on an account: invite the customer to the portal, see their status
export const PortalInviteCard = ({ customerId, email }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${customerId}/portal`);
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/customers/${customerId}/portal-invite`);
      setLink(data.link);
      toast.success(`Portal link emailed to ${data.email}`);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send the invite",
      );
    }
    setBusy(false);
  };

  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
      data-testid="portal-invite-card"
    >
      <div className="flex items-center gap-2">
        <UserCircle size={16} weight="bold" className="text-indigo-300" />
        <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-300">
          Customer portal
        </h2>
        {status?.active && (
          <span
            className="ml-auto rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300"
            data-testid="portal-invite-active"
          >
            Active
          </span>
        )}
      </div>
      <p className="mt-3 text-xs leading-relaxed text-zinc-400">
        {status?.active
          ? `${status.email}${
              status.last_login_at
                ? ` · last opened ${new Date(status.last_login_at).toLocaleDateString("en-US")}`
                : " · not opened yet"
            }${status.has_password ? " · password set" : ""}`
          : `Email ${email || "this account"} a private link to see quotes, drawings, slab layouts and job progress.`}
      </p>
      {status?.unread_messages > 0 && (
        <p
          className="mt-2 text-xs font-semibold text-indigo-300"
          data-testid="portal-invite-unread"
        >
          {status.unread_messages} unread customer note
          {status.unread_messages > 1 ? "s" : ""}
        </p>
      )}
      <button
        type="button"
        data-testid="portal-invite-send"
        disabled={busy}
        onClick={invite}
        className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-zinc-700 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:bg-zinc-800 disabled:opacity-40"
      >
        <PaperPlaneTilt size={14} weight="bold" />
        {busy
          ? "Sending…"
          : status?.active
            ? "Send a fresh link"
            : "Invite to the portal"}
      </button>
      {link && (
        <button
          type="button"
          data-testid="portal-invite-copy"
          onClick={() => {
            navigator.clipboard?.writeText(link);
            toast.success("Link copied");
          }}
          className="mt-2 flex w-full items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <Copy size={12} weight="bold" /> Copy the link
        </button>
      )}
    </div>
  );
};
